'use client';

import * as z from 'zod';
import { useState } from 'react';
import type { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Sizes, Text, Variants } from '@sola/ark-core/atoms/text/text.tsx';
import { Alert } from '@sola/ark-core/molecules/alert/alert.tsx';
import { EmailField } from '@sola/ark-core/molecules/email-field/email-field.tsx';
import { Fieldset } from '@sola/ark-core/molecules/fieldset/fieldset.tsx';
import { Form, FormSubmit } from '@sola/ark-core/molecules/form/form.tsx';
import { InputField } from '@sola/ark-core/molecules/input-field/input-field.tsx';
import { PostalCodeField } from '@sola/ark-core/molecules/postal-code-field/postal-code-field.tsx';
import { RadioGroup } from '@sola/ark-core/molecules/radio-group/radio-group.tsx';
import { StateField } from '@sola/ark-core/molecules/state-field/state-field.tsx';
import { cn } from '@sola/ark-core/utils/helpers.ts';
import { StateEnum } from '@sola/schema/dotcom/shared/geo.ts';
import { BlockProps } from '@sola/schema/lib/schema.ts';
import type {
  LeadChurchRequest,
  SubscriptionRequest,
} from '@sola/schema/lib/skippy.ts';
import {
  submitLeadChurchForm,
  submitSubscription,
} from '@sola/skippy-sdk/requests.ts';

import { NameFields } from '../../../molecules/name-fields/name-fields.tsx';
import { FormColumn } from '../../../organisms/forms/event-form/event-form.tsx';

const FormProps = BlockProps.extend({
  onSubmit: z
    .function({
      input: [z.custom<SubscriptionRequest>()],
      output: z.promise(z.boolean()),
    })
    .optional(),
  onSubmitLead: z
    .function({
      input: [z.custom<LeadChurchRequest>()],
      output: z.promise(z.boolean()),
    })
    .optional(),
  className: z.string().optional(),
});
type FormProps = z.infer<typeof FormProps>;

const FormState = z.enum(['submitting', 'error', 'success']).optional();
type FormState = z.infer<typeof FormState>;

const stateOptions = Object.entries(StateEnum).map(([abbr, name]) => ({
  text: name,
  value: abbr,
}));

export const PrayerPartner: React.FC<FormProps> = ({
  className,
  onSubmit = submitSubscription,
  onSubmitLead = submitLeadChurchForm('order', 'Prayer_Partner_Mail_Sign_Up'),
}) => {
  const { t } = useTranslation(['forms', 'skippy']);
  const [formState, setFormState] = useState<FormState>();
  const [contactMethod, setContactMethod] = useState('email');

  const handleSubmit = async (data: FieldValues) => {
    setFormState('submitting');
    try {
      if (contactMethod === 'email') {
        const success = await onSubmit({
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          subscriptionCode: 'Prayer_Partner_Email_Sign_Up',
        });
        setFormState(success ? 'success' : 'error');
        return;
      }

      const success = await onSubmitLead({
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        churchName: `${data.firstName} ${data.lastName}`,
        churchAddress: data.address,
        churchCity: data.city,
        churchState: data.state,
        churchPostal: data.postalCode,
        specialInstructions:
          'Please mail this person a prayer partner calendar monthly',
        items: [{ code: 'Prayer_Partner_Mail_Sign_Up', quantity: 1 }],
      } as LeadChurchRequest);
      setFormState(success ? 'success' : 'error');
    } catch {
      setFormState('error');
    }
  };
  return (
    <section
      className={cn('PrayerPartner component component--no-header', className)}
    >
      <div className="container">
        {formState === 'success' && (
          <Alert
            className="scroll-mt-roomy-md"
            variant="positive"
            title={t('skippy:form.prayerPartner.thanksTitle')}
            message={t('skippy:form.prayerPartner.thanksMessage')}
            scrollIntoView
            scrollBehavior="auto"
          />
        )}
        {formState === 'error' && (
          <Alert
            className="mb-comfy-xl scroll-mt-roomy-md"
            variant="negative"
            message={t('forms:prayerPartner.errorText')}
            scrollIntoView
          />
        )}
        {formState !== 'success' && (
          <Form
            onSubmit={handleSubmit}
            options={{ mode: 'onTouched' }}
            aria-label={t('forms:prayerPartner.formAriaLabel')}
          >
            <div className="PrayerPartner__form grid grid-col-1 md:items-end gap-comfy-lg md:gap-comfy-sm">
              <Fieldset
                id="contact-fields"
                header={t('skippy:form.prayerPartner.title')}
              >
                <Text size={Sizes.XSmall} variant={Variants.Heading}>
                  {t('skippy:form.prayerPartner.subtitle')}
                </Text>
                <FormColumn>
                  <NameFields />
                </FormColumn>

                <RadioGroup
                  defaultValue={'email'}
                  label={t('skippy:form.prayerPartner.contactMethodLabel')}
                  name="contactMethod"
                  onChange={({ target }) => setContactMethod(target.value)}
                  items={[
                    {
                      value: 'email',
                      text: t('forms:contactFields.emailLabel'),
                    },
                    {
                      value: 'address',
                      text: t('forms:addressFields.addressLabel'),
                    },
                  ]}
                />
              </Fieldset>
              <div>
                <Fieldset id="email-fields">
                  <EmailField
                    label={t('forms:contactFields.emailLabel')}
                    name="email"
                    required
                  />
                </Fieldset>
              </div>

              <div className={contactMethod === 'address' ? '' : 'hidden'}>
                <Fieldset id="address-fields">
                  <InputField
                    label={t('forms:addressFields.addressLabel')}
                    name="address"
                    autoComplete="street-address"
                    required={contactMethod === 'address'}
                  />
                  <div className="grid sm:grid-cols-2 gap-comfy-sm">
                    <InputField
                      label={t('forms:addressFields.cityLabel')}
                      name="city"
                      required={contactMethod === 'address'}
                    />
                    <StateField
                      label={t('forms:addressFields.stateLabel')}
                      name="state"
                      spellCheck={false}
                      placeholder={t('forms:addressFields.statePlaceholder')}
                      items={stateOptions}
                      required={contactMethod === 'address'}
                    />
                    <PostalCodeField
                      label={t('forms:addressFields.zipCodeLabel')}
                      name="postalCode"
                      autoComplete="postal-code"
                      required={contactMethod === 'address'}
                    />
                  </div>
                </Fieldset>
              </div>

              <FormSubmit
                className="PrayerPartner__button mt-comfy-lg"
                isBusy={formState === 'submitting'}
                disableUntilFilled
              >
                {t('skippy:form.prayerPartner.submit')}
              </FormSubmit>
            </div>
          </Form>
        )}
      </div>
    </section>
  );
};
